import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PioneerHoloCard } from "@shared/components/PioneerHoloCard";
import { RefChip } from "@/components/renewal/RefChip";
import { cn } from "@shared/lib/utils";
import type { MarketListing } from "@/lib/api";
import { signInWithTelegram } from "@/lib/auth";
import { cardArtSrc, cardSerialArtSrc } from "@/lib/cardArt";
import { finishMeta } from "@/lib/cards";
import { HOME_AGENTS } from "@/lib/homeAgents";
import { formatIp } from "./useMarketplace";

const AGENT_BY_ID = new Map(HOME_AGENTS.map((agent) => [agent.agentId, agent]));

/** `#serial/cap` tail — degrades gracefully when serial or cap is missing. */
export function serialLabel(serial: number | null, cap: number | null): string | null {
  if (serial == null) return null;
  return cap != null ? `#${serial}/${cap}` : `#${serial}`;
}

type ListingTileProps = {
  listing: MarketListing;
  /** This listing's mutation is in flight — its button is disabled. */
  pending: boolean;
  /** Signed-in state — signed-out Buy clicks route to sign-in instead of arming. */
  authenticated: boolean;
  onBuy: (listing: MarketListing) => void;
  onCancel: (listingId: number) => void;
};

const ACTION_BASE =
  "pointer-events-auto flex min-h-11 w-full items-center justify-center rounded-full px-3 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.1em] disabled:pointer-events-none disabled:opacity-60";

/**
 * One open listing — muted holo tile + mono caption/price. The action button
 * sits at a low idle opacity so the grid always signals it's actionable, and
 * brightens on hover/focus/press (always full opacity on coarse pointers): a
 * two-step accent Buy (`Buy · N IP` → `Confirm?`, auto-disarms after 4s), or a
 * neutral "Yours — cancel" when the viewer is the seller.
 */
export function ListingTile({ listing, pending, authenticated, onBuy, onCancel }: ListingTileProps) {
  const agent = AGENT_BY_ID.get(listing.agentId);
  const meta = finishMeta(listing.finish);
  const [armed, setArmed] = useState(false);

  // Two-step confirm disarms itself so a stale "Confirm?" never lingers.
  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(timer);
  }, [armed]);

  if (!agent) return null;

  const serial = serialLabel(listing.serial, meta.supplyCap);
  const nameFinish = [agent.name, meta.label].join(" · ");

  const handleBuyClick = () => {
    if (pending) return;
    if (!authenticated) {
      void signInWithTelegram("/marketplace").catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : "Telegram sign-in failed");
      });
      return;
    }
    if (!armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    onBuy(listing);
  };

  return (
    <article className="group flex flex-col gap-2">
      <div className="relative">
        <PioneerHoloCard
          // 판매 상품 = 해당 시리얼의 실카드 — 시리얼 아트 → finish 아트 → 초상 폴백
          imageSrc={
            cardSerialArtSrc(listing.agentId, listing.finish, listing.serial) ??
            cardArtSrc(listing.agentId, listing.finish) ??
            agent.portrait
          }
          imageAlt={`${agent.name} — ${meta.label}`}
          effect={meta.holoEffect}
          muted
          interactive={false}
          idleOpacityScale={0.5}
          loading="lazy"
        />
        {/* Action overlay — low-opacity at rest so the tile always signals it's
            actionable; brightens on hover/focus/press (coarse pointers always full). */}
        <div
          className={cn(
            "pointer-events-none absolute inset-x-2 bottom-2 z-10 transition-opacity duration-200 motion-reduce:transition-none",
            "opacity-70 group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100",
            (armed || pending) && "opacity-100",
          )}
        >
          {listing.mine ? (
            <button
              type="button"
              disabled={pending}
              onClick={() => onCancel(listing.id)}
              className={cn(
                ACTION_BASE,
                "border border-line bg-black/75 text-ink-dim transition-colors duration-200 hover:border-line-hi hover:text-ink",
              )}
            >
              {pending ? "Cancelling…" : "Yours — cancel"}
            </button>
          ) : (
            <button
              type="button"
              disabled={pending}
              onClick={handleBuyClick}
              aria-label={
                !authenticated
                  ? "Sign in to buy"
                  : armed
                    ? `Confirm purchase for ${formatIp(listing.price)} IP`
                    : `Buy for ${formatIp(listing.price)} IP`
              }
              className={cn(ACTION_BASE, "ty-cta tabular-nums")}
            >
              {pending
                ? "Buying…"
                : !authenticated
                  ? "Sign in to buy"
                  : armed
                    ? "Confirm?"
                    : `Buy · ${formatIp(listing.price)} IP`}
            </button>
          )}
        </div>
      </div>

      <div className="px-0.5">
        {/* Caption links through to the per-card market view (chart + asks).
            Name+finish truncates on one line; the serial — the product's core
            trust signal — gets its own line so it never gets clipped. */}
        <Link
          to={`/marketplace/${listing.catalogId}`}
          className={cn(
            "block font-mono text-[10px] font-medium uppercase leading-snug tracking-[0.08em] transition-colors duration-200 hover:text-ink",
            meta.accentText,
          )}
        >
          <span className="block truncate">{nameFinish}</span>
          {serial ? <span className="block truncate">{serial}</span> : null}
        </Link>
        {/* §2 타일 문법 — 가격은 항상 참조값(FMV) 칩과 쌍, 단독 숫자 금지 */}
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <p className="font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-ink-mute">
            <span className="tabular-nums text-ink">{formatIp(listing.price)}</span> IP
          </p>
          <RefChip>FMV —</RefChip>
        </div>
      </div>
    </article>
  );
}
